import React, { useState, useEffect, useMemo } from "react";
import {
    Card, CardBody, CardHeader, Nav, NavItem, NavLink,
    Table, Badge, Button, Alert,
    Pagination, PaginationItem, PaginationLink
} from "reactstrap";
import classnames from "classnames";
import { useNavigate } from "react-router-dom";
import { TableSkeleton, ColumnFilterRow, applyColumnFilters } from "../../Components/Common/DashboardKit";
import api from "../../api";
import { useCrList, useCrPendingCreation } from "./Form/useContractReviewApi";
import { fetchEpicorPrefill } from "./Form/useContractReviewApi";

/**
 * Landing-screen tab strip — the 6 tabs from Pass 1 §1.2:
 *   pendingCreation | awaiting | saved | inProgress | ready | mgr (manager only)
 *
 * Round 2 (CR legacy-parity repair) — two parity fixes applied here, both
 * scoped entirely to this file (no shared component touched):
 *
 *   1. EPO-style per-column filter row. Each grid now renders a filter input
 *      directly under each column header (reusing the shared DashboardKit
 *      ColumnFilterRow + applyColumnFilters that the EPO "Ready for Payment"
 *      page already uses). Filtering is client-side substring match against
 *      the already-loaded rows, per tab. Replaces the single global-search box
 *      from TableContainerReactTable.
 *
 *   2. Double-click-to-open, matching legacy ContractReviewNew.vb where every
 *      list grid (dgvSaved / dgvAwaitingMe / dgvManager / dgvReady /
 *      dgvPendingTask) opens the CR on CellDoubleClick. Each <tr> now has
 *      onDoubleClick → navigate to the CR (or, for Pending Creation, the
 *      Epicor-prefill new-CR flow). The CR# link button is preserved for
 *      single-click users.
 *
 * The previous TableContainerReactTable usage was removed in favor of the
 * EPO render pattern so the column-filter row and row double-click behave
 * exactly like the EPO grid the user pointed to.
 */
export default function ContractReviewTabs() {
    const navigate = useNavigate();
    const [isManager, setIsManager] = useState(false);

    useEffect(() => {
        (async () => {
            try { await api.get("ContractReview/list?tab=mgr"); setIsManager(true); }
            catch { setIsManager(false); }
        })();
    }, []);

    const [active, setActive] = useState("awaiting");

    // Always-loaded tabs so badge counts show on the headers.
    const awaitingQ = useCrList("awaiting");
    const savedQ = useCrList("saved");
    const inProgressQ = useCrList("inProgress");
    const readyQ = useCrList("ready");
    const archivedQ = useCrList("archived");
    const mgrQ = useCrList("mgr", isManager);
    const pendingQ = useCrPendingCreation();

    // Double-click handler for a standard CR row → open the CR detail/form.
    const openCr = (row) => {
        const id = row?.contractReviewId;
        if (id != null) navigate(`/contract-review/${id}`);
    };

    // Double-click handler for a Pending-Creation row → start a new CR from the
    // Epicor SO/Quote, mirroring the existing single-click prefill behavior.
    const openPending = async (row) => {
        if (row?.orderNum) {
            try { await fetchEpicorPrefill({ soNum: row.orderNum }); } catch { /* prefill is best-effort */ }
            navigate(`/contract-review/new?soNum=${row.orderNum}`);
        } else if (row?.quoteNum) {
            navigate(`/contract-review/new?quoteNum=${row.quoteNum}`);
        }
    };

    const stdCols = useMemo(() => standardColumns(navigate), [navigate]);
    const mgrCols = useMemo(() => mgrColumns(navigate), [navigate]);
    const archivedCols = useMemo(() => archivedColumns(navigate), [navigate]);
    const pendCols = useMemo(() => pendingColumns(navigate), [navigate]);

    const Tab = ({ k, label, count }) => (
        <NavItem>
            <NavLink href="#" className={classnames("px-3 py-2", { active: active === k })}
                onClick={(e) => { e.preventDefault(); setActive(k); }}>
                <span className="me-2">{label}</span>
                {count != null && count !== "" && <Badge pill>{count}</Badge>}
            </NavLink>
        </NavItem>
    );

    return (
        <Card>
            <CardHeader>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <Nav tabs className="nav-tabs-custom card-header-tabs border-bottom-0">
                        <Tab k="pending" label="Pending Creation" count={pendingQ.data?.length ?? ""} />
                        <Tab k="awaiting" label="Awaiting My Response" count={awaitingQ.data?.length ?? ""} />
                        <Tab k="saved" label="Saved for Later" count={savedQ.data?.length ?? ""} />
                        <Tab k="inProgress" label="In Progress" count={inProgressQ.data?.length ?? ""} />
                        <Tab k="ready" label="Ready" count={readyQ.data?.length ?? ""} />
                        <Tab k="archived" label="Archived" count={archivedQ.data?.length ?? ""} />
                        {isManager && <Tab k="mgr" label="My Dept's Active CRs" count={mgrQ.data?.length ?? ""} />}
                    </Nav>

                    <Button color="primary" size="sm" onClick={() => navigate("/contract-review/new")}>
                        <i className="ri-add-line me-1" /> New CR
                    </Button>
                </div>
            </CardHeader>

            <CardBody>
                {active === "pending" && (
                    <FilterGrid
                        rows={pendingQ.data} columns={pendCols}
                        loading={pendingQ.isLoading} error={pendingQ.error}
                        onRowDoubleClick={openPending}
                        emptyText="No SOs or Quotes flagged for Contract Review."
                    />
                )}
                {active === "awaiting" && (
                    <FilterGrid rows={awaitingQ.data} columns={stdCols}
                        loading={awaitingQ.isLoading} error={awaitingQ.error} onRowDoubleClick={openCr} />
                )}
                {active === "saved" && (
                    <FilterGrid rows={savedQ.data} columns={stdCols}
                        loading={savedQ.isLoading} error={savedQ.error} onRowDoubleClick={openCr} />
                )}
                {active === "inProgress" && (
                    <FilterGrid rows={inProgressQ.data} columns={stdCols}
                        loading={inProgressQ.isLoading} error={inProgressQ.error} onRowDoubleClick={openCr} />
                )}
                {active === "ready" && (
                    <FilterGrid rows={readyQ.data} columns={stdCols}
                        loading={readyQ.isLoading} error={readyQ.error} onRowDoubleClick={openCr} />
                )}
                {active === "archived" && (
                    <FilterGrid rows={archivedQ.data} columns={archivedCols}
                        loading={archivedQ.isLoading} error={archivedQ.error} onRowDoubleClick={openCr}
                        emptyText="No archived contract reviews yet." />
                )}
                {active === "mgr" && isManager && (
                    <FilterGrid rows={mgrQ.data} columns={mgrCols}
                        loading={mgrQ.isLoading} error={mgrQ.error} onRowDoubleClick={openCr} />
                )}
            </CardBody>
        </Card>
    );
}

/**
 * Generic filterable, paginated, double-clickable grid following the EPO
 * "Ready for Payment" render pattern (thead + ColumnFilterRow + tbody).
 *
 * `columns` shape:
 *   { id, label, type?: "text"|"select"|"boolean"|"none",
 *     get?: (row) => value-used-for-filter,
 *     render?: (row) => cell-content,
 *     placeholder?, className? }
 */
function FilterGrid({ rows, columns, loading, error, onRowDoubleClick, emptyText }) {
    const [columnFilters, setColumnFilters] = useState({});
    const [page, setPage] = useState(1);
    const pageSize = 10;

    // Reset paging when the filters or the underlying data change.
    useEffect(() => { setPage(1); }, [columnFilters, rows]);

    const filtered = useMemo(
        () => applyColumnFilters(rows || [], columns, columnFilters),
        [rows, columns, columnFilters]
    );

    if (loading) {
        return (
            <Table className="table-centered align-middle table-nowrap mb-0">
                <thead className="table-light">
                    <tr>{columns.map((c) => <th key={c.id}>{c.label}</th>)}</tr>
                </thead>
                <tbody><TableSkeleton rows={5} columns={columns.length} /></tbody>
            </Table>
        );
    }
    if (error) return <Alert color="danger">Could not load data.</Alert>;

    const total = filtered.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages);
    const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

    const hasAnyRows = (rows || []).length > 0;

    return (
        <div className="table-responsive table-card mb-0">
            <Table hover className="table-centered align-middle table-nowrap mb-0">
                <thead className="table-light">
                    <tr>
                        {columns.map((c) => <th key={c.id} className={c.className}>{c.label}</th>)}
                    </tr>
                    <ColumnFilterRow
                        columns={columns}
                        filters={columnFilters}
                        onChange={setColumnFilters}
                    />
                </thead>
                <tbody>
                    {paged.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} className="text-center text-muted py-4">
                                <i className="ri-inbox-line fs-3 d-block mb-2"></i>
                                {hasAnyRows
                                    ? "No rows match the current filters."
                                    : (emptyText || "Nothing to show here.")}
                            </td>
                        </tr>
                    ) : (
                        paged.map((row, i) => (
                            <tr
                                key={row.contractReviewId ?? row.orderNum ?? row.quoteNum ?? i}
                                onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(row)}
                                style={{ cursor: onRowDoubleClick ? "pointer" : "default" }}
                            >
                                {columns.map((c) => (
                                    <td key={c.id} className={c.className}>
                                        {c.render ? c.render(row) : valueOf(row, c)}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>

            {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center px-2 py-2">
                    <small className="text-muted">
                        {total} row{total === 1 ? "" : "s"} · page {safePage} of {totalPages}
                    </small>
                    <Pagination className="mb-0" size="sm">
                        <PaginationItem disabled={safePage <= 1}>
                            <PaginationLink previous onClick={() => setPage(safePage - 1)} />
                        </PaginationItem>
                        <PaginationItem disabled={safePage >= totalPages}>
                            <PaginationLink next onClick={() => setPage(safePage + 1)} />
                        </PaginationItem>
                    </Pagination>
                </div>
            )}
        </div>
    );
}

function valueOf(row, c) {
    const v = c.get ? c.get(row) : row[c.id];
    return v == null ? "" : v;
}

// ---- column definitions (same data as before, EPO-pattern shape) ----------

function crLink(navigate, value) {
    return (
        <button type="button" className="btn btn-link p-0 text-decoration-none fw-semibold"
            onClick={() => navigate(`/contract-review/${value}`)}>
            {value}
        </button>
    );
}

function standardColumns(navigate) {
    return [
        { id: "contractReviewId", label: "CR #", get: (r) => r.contractReviewId, render: (r) => crLink(navigate, r.contractReviewId) },
        { id: "customer", label: "Customer" },
        { id: "title", label: "Title", render: (r) => <span title={r.title}>{r.title}</span> },
        { id: "salesOrderNum", label: "SO #", get: (r) => r.salesOrderNum || "" },
        { id: "quoteNumber", label: "Quote #", get: (r) => r.quoteNumber || "" },
        { id: "createdByName", label: "Created By" },
        { id: "createdOn", label: "Created On", get: (r) => fmtDate(r.createdOn), render: (r) => fmtDate(r.createdOn) },
        { id: "needBy", label: "Need By", get: (r) => fmtDate(r.needBy), render: (r) => fmtDate(r.needBy) },
    ];
}

function mgrColumns(navigate) {
    return [
        { id: "contractReviewId", label: "CR #", get: (r) => r.contractReviewId, render: (r) => crLink(navigate, r.contractReviewId) },
        { id: "customer", label: "Customer" },
        { id: "title", label: "Title" },
        { id: "salesOrderNum", label: "SO #", get: (r) => r.salesOrderNum || "" },
        { id: "quoteNumber", label: "Quote #", get: (r) => r.quoteNumber || "" },
        { id: "assignedToName", label: "Assigned To" },
        { id: "needBy", label: "Need By", get: (r) => fmtDate(r.needBy), render: (r) => fmtDate(r.needBy) },
    ];
}

function archivedColumns(navigate) {
    // The backend reuses assignedToName to carry the Won/Lost disposition for
    // archived rows (no DTO change). Render it as a coloured "Disposition" badge.
    return [
        { id: "contractReviewId", label: "CR #", get: (r) => r.contractReviewId, render: (r) => crLink(navigate, r.contractReviewId) },
        { id: "customer", label: "Customer" },
        { id: "title", label: "Title" },
        { id: "salesOrderNum", label: "SO #", get: (r) => r.salesOrderNum || "" },
        { id: "quoteNumber", label: "Quote #", get: (r) => r.quoteNumber || "" },
        {
            id: "assignedToName", label: "Disposition", type: "select",
            get: (r) => r.assignedToName || "—",
            render: (r) => {
                const d = r.assignedToName || "—";
                const color = d === "Won" ? "success" : (d === "Lost" ? "danger" : "secondary");
                return <Badge color={color} pill>{d}</Badge>;
            }
        },
        { id: "createdByName", label: "Created By" },
        { id: "createdOn", label: "Created On", get: (r) => fmtDate(r.createdOn), render: (r) => fmtDate(r.createdOn) },
    ];
}

function pendingColumns(navigate) {
    return [
        { id: "customer", label: "Customer" },
        { id: "salesRep", label: "Sales Rep" },
        { id: "soEnteredBy", label: "Entered By" },
        {
            id: "orderNum", label: "SO #", get: (r) => r.orderNum || "",
            render: (r) => r.orderNum
                ? (
                    <button type="button" className="btn btn-link p-0 text-decoration-none fw-semibold"
                        onClick={async () => {
                            try { await fetchEpicorPrefill({ soNum: r.orderNum }); } catch { /* best-effort */ }
                            navigate(`/contract-review/new?soNum=${r.orderNum}`);
                        }}>
                        {r.orderNum}
                    </button>
                ) : ""
        },
        {
            id: "quoteNum", label: "Quote #", get: (r) => r.quoteNum || "",
            render: (r) => r.quoteNum
                ? (
                    <button type="button" className="btn btn-link p-0 text-decoration-none fw-semibold"
                        onClick={() => navigate(`/contract-review/new?quoteNum=${r.quoteNum}`)}>
                        {r.quoteNum}
                    </button>
                ) : ""
        },
        { id: "requestedShipDate", label: "Requested Ship Date", get: (r) => fmtDate(r.requestedShipDate), render: (r) => fmtDate(r.requestedShipDate) },
    ];
}

function fmtDate(s) {
    if (!s) return "";
    try {
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return s;
        return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
    } catch { return s; }
}
